import { useEffect, useRef, useState } from 'react';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import MenuItem from '@mui/material/MenuItem';
import Button from '@mui/material/Button';
import Divider from '@mui/material/Divider';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import { bugImagesApi, bugsApi } from '../../api/acceptanceApi';
import { getApiErrorMessage } from '../../api/client';
import { notify } from '../../components/Notifier';
import Typography from '@mui/material/Typography';
import { BUG_STATUS_META, BUG_STATUS_OPTIONS } from './statusMeta';
import { BUG_SEVERITY_META, BUG_SEVERITY_OPTIONS } from './severityMeta';
import { ImageUploader } from '../../components/ImageUploader';
import { SavedImageGrid } from '../../components/SavedImageGrid';
import { resolveBugImageUrl } from '../../api/client';
import { useAuth } from '../../auth/AuthContext';
import { usePermission } from '../../auth/usePermission';
import type { Bug, BugImage, BugSeverity, BugStatus } from '../../types';

/** input type="datetime-local" cần "yyyy-MM-ddTHH:mm" theo giờ local, không phải ISO/UTC. */
function formatNowForInput(): string {
  const now = new Date();
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}T${pad(now.getHours())}:${pad(now.getMinutes())}`;
}

interface BugFormDialogProps {
  open: boolean;
  /** Tính năng được gắn bug (bắt buộc khi tạo mới). */
  featureId: string;
  featureName?: string;
  /** Có giá trị = chế độ sửa, null = thêm mới. */
  bug: Bug | null;
  onClose: () => void;
  onSaved: (bug: Bug) => void;
}

/** Dialog thêm / sửa một bug: tiêu đề, mô tả chi tiết, trạng thái. */
export function BugFormDialog({
  open,
  featureId,
  featureName,
  bug,
  onClose,
  onSaved,
}: BugFormDialogProps): JSX.Element {
  const { roles } = useAuth();
  const canEditSeverity = usePermission('BUG_SEVERITY_EDIT');
  // Xấp xỉ "role bug_source=CUSTOMER" ở phía client: user CHỈ giữ role Customer
  // (khớp isCustomerOnly ở backend). Giá trị thật luôn được backend ép lại khi
  // tạo bug (xem routes/bugs.ts handleCreate) — ở đây chỉ để điều khiển UI.
  const isCustomerSourced = roles.length > 0 && roles.every((r) => r === 'Customer');

  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [status, setStatus] = useState<BugStatus>('OPEN');
  const [severity, setSeverity] = useState<BugSeverity | ''>('');
  const [reportedByCustomer, setReportedByCustomer] = useState(isCustomerSourced);
  const [discoveredAt, setDiscoveredAt] = useState('');
  const [discoveredNote, setDiscoveredNote] = useState('');
  const [saving, setSaving] = useState(false);
  const [existingImages, setExistingImages] = useState<BugImage[]>([]);
  const [pendingFiles, setPendingFiles] = useState<File[]>([]);
  const savingRef = useRef(false);

  useEffect(() => {
    if (!open) return;
    setTitle(bug?.title ?? '');
    setDescription(bug?.description ?? '');
    setStatus(bug?.status ?? 'OPEN');
    setSeverity(bug?.severity ?? '');
    setReportedByCustomer(bug ? bug.source === 'CUSTOMER' : isCustomerSourced);
    // input type="datetime-local" cần "yyyy-MM-ddTHH:mm", bỏ giây/timezone của ISO.
    setDiscoveredAt(bug?.discovered_at ? bug.discovered_at.slice(0, 16) : '');
    setDiscoveredNote(bug?.discovered_note ?? '');
    setPendingFiles([]);
    setExistingImages([]);
    if (bug) {
      // Chế độ sửa: nạp ảnh đã lưu để xem lại và xoá bớt.
      void bugImagesApi
        .list(bug.id)
        .then(setExistingImages)
        .catch(() => notify('Không tải được ảnh đính kèm', 'warning'));
    }
  }, [open, bug]);

  const handleRemoveExisting = async (image: BugImage) => {
    try {
      await bugImagesApi.remove(image.id);
      setExistingImages((prev) => prev.filter((i) => i.id !== image.id));
    } catch (err) {
      notify(getApiErrorMessage(err, 'Không thể xoá ảnh'), 'error');
    }
  };

  const handleSave = async () => {
    // Chặn gọi lại nếu đang lưu (chống double-submit)
    if (savingRef.current) {
      return;
    }

    if (title.trim().length === 0) {
      notify('Vui lòng nhập tiêu đề lỗi', 'warning');
      return;
    }

    savingRef.current = true;
    setSaving(true);
    try {
      const body = {
        title: title.trim(),
        description: description.trim(),
        status,
        severity: canEditSeverity ? severity || null : bug?.severity ?? null,
        // Backend vẫn ép CUSTOMER nếu role bug_source=CUSTOMER dù gửi gì;
        // role khác (team/admin) sửa được nguồn phát hiện kể cả khi edit.
        source: reportedByCustomer ? ('CUSTOMER' as const) : ('TEAM' as const),
        discovered_at: discoveredAt ? new Date(discoveredAt).toISOString() : null,
        discovered_note: reportedByCustomer && !isCustomerSourced ? discoveredNote.trim() : null,
      };
      const saved = bug ? await bugsApi.update(bug.id, body) : await bugsApi.create(featureId, body);

      // Upload sau khi bug đã tồn tại: nếu upload fail thì giữ dialog mở để user thử lại.
      // Nếu không có ảnh cần upload, đóng dialog ngay.
      if (pendingFiles.length > 0) {
        try {
          await bugImagesApi.upload(saved.id, pendingFiles);
          // Upload thành công → đóng dialog
          notify(bug ? 'Đã cập nhật lỗi' : 'Đã thêm lỗi — tính năng chuyển sang "Đang sửa lỗi"', 'success');
          onSaved(saved);
        } catch (err) {
          // Upload thất bại → không đóng dialog, giữ để user thử lại
          notify(getApiErrorMessage(err, 'Upload ảnh thất bại. Vui lòng thử lại'), 'error');
          savingRef.current = false;
          setSaving(false);
        }
      } else {
        // Không có ảnh → đóng dialog ngay
        notify(bug ? 'Đã cập nhật lỗi' : 'Đã thêm lỗi — tính năng chuyển sang "Đang sửa lỗi"', 'success');
        savingRef.current = false;
        onSaved(saved);
      }
    } catch (err) {
      notify(getApiErrorMessage(err, 'Không thể lưu lỗi'), 'error');
      savingRef.current = false;
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={saving ? undefined : onClose} fullWidth maxWidth="sm">
      <DialogTitle>
        {bug ? 'Sửa lỗi' : 'Thêm lỗi'}
        {featureName ? ` — ${featureName}` : ''}
      </DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <TextField
            label="Tiêu đề"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            fullWidth
            size="small"
            required
            autoFocus
            inputProps={{ maxLength: 300 }}
          />
          <TextField
            label="Mô tả chi tiết"
            placeholder="Các bước tái hiện, kết quả mong đợi, kết quả thực tế…"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            fullWidth
            multiline
            minRows={5}
            size="small"
          />
          <Stack direction="row" spacing={2} sx={{ flexWrap: 'wrap', rowGap: 2 }}>
            <TextField
              select
              label="Trạng thái"
              value={status}
              onChange={(e) => setStatus(e.target.value as BugStatus)}
              size="small"
              sx={{ minWidth: 180, flex: '1 1 180px' }}
            >
              {BUG_STATUS_OPTIONS.map((s) => (
                <MenuItem key={s} value={s}>
                  {BUG_STATUS_META[s].label}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              select
              label="Độ nghiêm trọng"
              value={severity}
              onChange={(e) => setSeverity(e.target.value as BugSeverity | '')}
              size="small"
              sx={{ minWidth: 180, flex: '1 1 180px' }}
              SelectProps={{ displayEmpty: true }}
              InputLabelProps={{ shrink: true }}
              disabled={!canEditSeverity}
              helperText={!canEditSeverity ? 'Bạn không có quyền chỉnh mức độ nghiêm trọng' : undefined}
            >
              <MenuItem value="">Chưa phân loại</MenuItem>
              {BUG_SEVERITY_OPTIONS.map((s) => (
                <MenuItem key={s} value={s}>
                  {BUG_SEVERITY_META[s].label}
                </MenuItem>
              ))}
            </TextField>
          </Stack>
          <FormControlLabel
            control={
              <Checkbox
                checked={reportedByCustomer}
                disabled={isCustomerSourced}
                onChange={(e) => setReportedByCustomer(e.target.checked)}
              />
            }
            label="Bug do khách hàng phát hiện"
          />
          <Stack direction="row" spacing={1} alignItems="center">
            <Tooltip title="Nếu không nhập thì tính là thời điểm hiện tại">
              <TextField
                label="Ngày giờ phát hiện"
                type="datetime-local"
                value={discoveredAt}
                onChange={(e) => setDiscoveredAt(e.target.value)}
                size="small"
                InputLabelProps={{ shrink: true }}
                sx={{ maxWidth: 280 }}
              />
            </Tooltip>
            <Tooltip title="Chọn thời điểm hiện tại">
              <IconButton size="small" onClick={() => setDiscoveredAt(formatNowForInput())}>
                <AccessTimeIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Stack>
          {reportedByCustomer && !isCustomerSourced && (
            <TextField
              label="Ghi chú/người phát hiện"
              placeholder="VD: khách A báo qua Zalo"
              value={discoveredNote}
              onChange={(e) => setDiscoveredNote(e.target.value)}
              fullWidth
              size="small"
              inputProps={{ maxLength: 500 }}
            />
          )}
          <Divider />
          <Typography variant="subtitle2" fontWeight={700}>
            Ảnh đính kèm
          </Typography>
          {/* Chế độ sửa: ảnh đã lưu xoá được ngay, tách khỏi ảnh mới chọn. */}
          <SavedImageGrid
            images={existingImages}
            resolveUrl={resolveBugImageUrl}
            onRemove={saving ? undefined : (img) => void handleRemoveExisting(img)}
          />
          <ImageUploader value={pendingFiles} onChange={setPendingFiles} disabled={saving} />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>
          Huỷ
        </Button>
        <Button variant="contained" onClick={() => void handleSave()} disabled={saving}>
          {saving ? 'Đang lưu…' : 'Lưu'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
